import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface Tenant {
  id: string;
  name: string;
  display_name: string;
  description: string;
}

interface TenantContextType {
  selectedTenant: Tenant | null;
  setSelectedTenant: (tenant: Tenant | null) => void;
  isLoading: boolean;
}

// Default tenant for Ford - used when no tenant is explicitly selected
const DEFAULT_TENANT: Tenant = {
  id: 'default-ford',
  name: 'ford',
  display_name: 'Ford',
  description: 'Default Ford tenant',
};

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

interface TenantProviderProps {
  children: ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const [selectedTenant, setSelectedTenantState] = useState<Tenant | null>(DEFAULT_TENANT);
  const [isLoading, setIsLoading] = useState(true);

  // Load tenant from localStorage on mount
  useEffect(() => {
    const storedTenant = localStorage.getItem('selectedTenant');
    if (storedTenant) {
      try {
        const tenant = JSON.parse(storedTenant);
        setSelectedTenantState(tenant);
      } catch (error) {
        console.error('Failed to parse stored tenant:', error);
        localStorage.removeItem('selectedTenant');
        // Fall back to default tenant on error
        setSelectedTenantState(DEFAULT_TENANT);
      }
    }
    setIsLoading(false);
  }, []);

  const setSelectedTenant = (tenant: Tenant | null) => {
    setSelectedTenantState(tenant);

    if (tenant) {
      localStorage.setItem('selectedTenant', JSON.stringify(tenant));
    } else {
      localStorage.removeItem('selectedTenant');
    }
  };

  return (
    <TenantContext.Provider
      value={{
        selectedTenant,
        setSelectedTenant,
        isLoading
      }}
    >
      {children}
    </TenantContext.Provider>
  );
};
